const reportError = require('airship/modules/error-reporting')

const uaStream = require('./ua-stream')

module.exports = createRequest

/**
 * Instantiates the request library.
 * @param {Object} [defaultOptions = {}] options that should be merged into the
 *   options of any request made through the client.
 * @returns {Function} the request function and its convenience methods
 */
function createRequest(defaultOptions = {}) {
  const requestStream = uaStream()

  let inFlightRequests = {}

  requestStream.on('data', onRequestComplete)
  requestStream.on('error', onRequestError)

  return Object.assign(request, {
    get: getRequest,
    post: postRequest,
    put: putRequest,
    patch: patchRequest,
    delete: deleteRequest,
    end
  })

  /**
   * Ends the underlying request stream; rejects any in-flight requests.
   */
  function end() {
    const toReject = inFlightRequests

    inFlightRequests = {}

    Object.keys(toReject).forEach(key => {
      toReject[key].reject(
        new Error('Client was ended before the request completed')
      )
    })

    requestStream.end()
  }

  /**
   * Pops a request from the list of in-flight requests.
   * @param {Number|String} id the request id
   * @returns {Object} the resolution object, with {reject, resolve}
   */
  function popResolution(id) {
    const resolution = inFlightRequests[id]

    delete inFlightRequests[id]

    return resolution
  }

  /**
   * Event handler for completed requests.
   * @param {Object} data the completed request
   */
  function onRequestComplete(data) {
    const resolution = popResolution(data.requestId)

    if (resolution) {
      resolution.resolve(data)
    }
  }

  /**
   * Event handler for request errors. Rejects the appropriate in-flight request
   * @param {Error} err
   */
  function onRequestError(err) {
    let resolution

    if (err && err.request) {
      resolution = popResolution(err.request.requestId)
    }

    if (!resolution) {
      return reportError(err, __filename, defaultOptions)
    }

    resolution.reject(err)
  }

  /**
   * Performs a request
   * @param {Object} _options the options object, as defined in the underlying
   *   request2 client
   * @param {Function} [ready] optional callback; in the event that a callback
   *   is not provided, a promise is returned.
   * @returns {Promise|undefined}
   */
  function request(_options, ready) {
    const requestId = _options.requestId || createRandomId()
    const options = Object.assign({}, defaultOptions, _options, {requestId})

    if (!ready) {
      return new Promise(begin)
    }

    begin(ready.bind(this, null), ready)

    function begin(resolve, reject) {
      if (inFlightRequests[requestId]) {
        return reject(
          new Error(
            `Cannot create a request with a duplicate requestId: ${requestId}`
          )
        )
      }

      inFlightRequests[requestId] = {reject, resolve}
      requestStream.write(options)
    }
  }

  /**
   * Convenience method for performing a GET request
   * @param options
   * @param ready
   * @returns {Promise|undefined}
   */
  function getRequest(options = {}, ready) {
    options.method = 'get'
    return request(options, ready)
  }

  /**
   * Convenience method for performing a POST
   * @param options
   * @param ready
   * @returns {Promise|undefined}
   */
  function postRequest(options = {}, ready) {
    options.method = 'post'
    return request(options, ready)
  }

  /**
   * Convenience method for performing a PUT
   * @param options
   * @param ready
   * @returns {Promise|undefined}
   */
  function putRequest(options = {}, ready) {
    options.method = 'put'
    return request(options, ready)
  }

  /**
   * Convenience method for performing a PATCH
   * @param options
   * @param ready
   * @returns {Promise|undefined}
   */
  function patchRequest(options = {}, ready) {
    options.method = 'patch'
    return request(options, ready)
  }

  /**
   * Convenience method for performing a DELETE
   * @param options
   * @param ready
   * @returns {Promise|undefined}
   */
  function deleteRequest(options = {}, ready) {
    options.method = 'delete'
    return request(options, ready)
  }
}

/**
 * Creates a random integer ID, between 0 and MAX_SAFE_INTEGER
 * @returns {Number} the random number
 */
function createRandomId() {
  return Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)
}
